import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePublishLanguageAlternates } from '../app/language-alternates';
import { ApiError } from '../core/api/api-error';
import { getArticle } from '../core/api/content-service';
import { formatPlainDate } from '../core/dates/plain-date';
import { useLanguage } from '../core/hooks/use-language';
import { usePageMetadata } from '../core/hooks/use-page-metadata';
import { useTranslation } from '../core/hooks/use-translation';
import type { PublicArticle } from '../core/models/public-article';
import { EmptyState } from '../ui/empty-state';
import { ShareBar } from '../ui/share-bar';
import { Skeleton } from '../ui/skeleton';
import { ArrowLeftIcon, DocumentIcon } from '../ui/portal-icons';
import { buildAlternatePaths } from './build-alternate-paths';

/** What the URL turned out to be, which on this page is not always "an article". */
type Outcome = 'loading' | 'found' | 'gone' | 'missing' | 'failed';

/**
 * One article.
 *
 * **Three ways for a URL here to not be an article, and they are told apart on purpose.** A
 * withdrawn article answers 410 and says so — somebody followed a real link, and "not found" would
 * be a lie about a page that existed. An unknown slug answers 404. A retired slug answers 301, and
 * `fetch` follows it, so the article that comes back is at a different address from the one asked
 * for; the address bar is corrected with a replacement rather than a push, so the back button does
 * not walk into the redirect again.
 *
 * The language switch is published rather than left to the header, because an article's slug is
 * per language: rewriting `/kk` to `/ru` on this page produces a Russian URL carrying a Kazakh
 * slug. Where the counterpart is not published, the switch goes to that language's blog index —
 * the honest destination, and not a 404.
 */
export function ArticlePage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [article, setArticle] = useState<PublicArticle | null>(null);
    const [outcome, setOutcome] = useState<Outcome>('loading');

    useEffect(() => {
        if (slug === undefined) {
            return;
        }

        const controller = new AbortController();

        setOutcome('loading');

        getArticle(language, slug, controller.signal)
            .then((loaded) => {
                setArticle(loaded);
                setOutcome('found');

                // The redirect was already followed by fetch; this is what puts the canonical
                // address in the bar so the reader copies the right link.
                if (loaded.slug !== slug) {
                    void navigate(`/${language}/blog/${loaded.slug}`, { replace: true });
                }
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }

                setArticle(null);

                if (cause instanceof ApiError && cause.status === 410) {
                    setOutcome('gone');
                } else if (cause instanceof ApiError && cause.status === 404) {
                    setOutcome('missing');
                } else {
                    setOutcome('failed');
                }
            });

        return () => controller.abort();
    }, [language, slug, navigate]);

    const alternatePaths = article === null ? {} : buildAlternatePaths(article, language);

    usePublishLanguageAlternates(
        article === null
            ? null
            : {
                  kk: alternatePaths.kk ?? '/kk/blog',
                  ru: alternatePaths.ru ?? '/ru/blog'
              }
    );

    usePageMetadata({
        title: article === null ? `${t('blog.title')} — ${t('brand.name')}` : article.seoTitle,
        description: article?.seoDescription,
        canonicalPath: article === null ? undefined : `/${language}/blog/${article.slug}`,
        alternatePaths,
        imageUrl: article?.coverImageUrl,
        type: 'article',
        publishedAt: article?.publishedAt,
        modifiedAt: article?.updatedAt
    });

    if (outcome === 'loading') {
        // The shape of an article rather than the word "loading": a heading, a date and a column of
        // text is what is coming, and drawing it keeps the page from jumping when it lands.
        return (
            <div role="status" aria-live="polite" className="mx-auto max-w-3xl space-y-4 px-4 py-12">
                <span className="sr-only">{t('common.loading')}</span>
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-56 rounded-2xl" />
                <Skeleton className="h-4" />
                <Skeleton className="h-4" />
                <Skeleton className="h-4 w-2/3" />
            </div>
        );
    }

    if (outcome !== 'found' || article === null) {
        return (
            <div className="mx-auto max-w-2xl px-4 py-20">
                <EmptyState
                    icon={<DocumentIcon />}
                    title={t(outcome === 'gone' ? 'article.gone.title' : 'article.missing.title')}
                    action={
                        <Link
                            to={`/${language}/blog`}
                            className="inline-flex rounded-xl bg-brand-600 px-5 py-2.5 font-semibold text-white transition hover:bg-brand-700"
                        >
                            {t('article.back_to_blog')}
                        </Link>
                    }
                >
                    {t(outcome === 'gone' ? 'article.gone.body' : 'article.missing.body')}
                </EmptyState>
            </div>
        );
    }

    return (
        <article className="mx-auto max-w-3xl px-4 py-12">
            <Link
                to={`/${language}/blog`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline dark:text-brand-400"
            >
                <ArrowLeftIcon className="size-4" />
                {t('article.back_to_blog')}
            </Link>

            <h1 className="mt-4 text-3xl font-extrabold text-balance text-slate-900 sm:text-4xl dark:text-slate-100">
                {article.title}
            </h1>

            <time
                className="mt-3 block text-sm text-slate-500 dark:text-slate-400"
                dateTime={article.publishedAt}
            >
                {formatPlainDate(article.publishedAt.slice(0, 10), language, {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                })}
            </time>

            {article.coverImageUrl !== null && (
                <img
                    src={article.coverImageUrl}
                    alt={article.coverImageAlt ?? ''}
                    className="mt-8 w-full rounded-2xl object-cover"
                    loading="lazy"
                />
            )}

            {/*
                The body is HTML the server sanitised on save, against an allow-list, with script,
                style and iframe elements removed outright. Sanitising here instead would prove
                nothing: this same body is what a renderer running outside a browser would emit.
            */}
            <div
                className="article-body mt-8 text-slate-800 dark:text-slate-300"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: article.body }}
            />

            <ShareBar
                path={`/${language}/blog/${article.slug}`}
                title={article.title}
                campaign="blog"
                content={article.slug}
            />

            {/*
                The article's job is to bring a reader to the wizard, so the call to action is a
                link to the booking page — which is rate-limited, prerendered and already the place
                the form lives. An inline booking form on every article would multiply the surface
                the cutover has to watch.
            */}
            <aside className="mt-12 rounded-2xl border border-brand-100 bg-brand-50 px-6 py-8 text-center dark:border-brand-900 dark:bg-brand-950">
                <h2 className="text-xl font-bold text-brand-900 dark:text-brand-200">
                    {t('article.cta.title')}
                </h2>
                <p className="mt-2 text-brand-800 dark:text-brand-300">{t('article.cta.body')}</p>
                <Link
                    to={`/${language}/book`}
                    className="mt-5 inline-flex rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white transition hover:bg-brand-700"
                >
                    {t('article.cta.action')}
                </Link>
            </aside>
        </article>
    );
}
